import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Ban,
} from 'lucide-react';

/**
 * MonoSyncProgress - SLIM redirect / status dialog for Mono bank connect.
 *
 * iter959 post-signoff cleanup: the confirm-fee modal was moved server-side to
 * the backlog_jobs orchestrator (which auto-charges without a modal and drives
 * the persistent global banner). This component is now a slim status splash
 * for the brief window between "user clicked Connect" and "orchestrator owns
 * the sync (banner takes over)".
 *
 * Retained sync states:
 *   syncing                  → spinner + "safe to close, banner takes over"
 *   completed / no_backlog   → success confirmation
 *   syncing_in_background    → server backfill still running notice
 *   transactions_unavailable → upload-statement-instead notice
 *   failed                   → reconnect prompt
 *
 * Removed (dead code): backlog_required, payment_failed, preflight, locked
 * period date picker, confirm-fee button, retry-now/rollback flow. All of
 * these are now handled by:
 *   • services/backlog_jobs.py orchestrator (auto-charge in background)
 *   • BacklogProgressBanner (global progress + terminal toasts)
 *   • RetryBacklogButton (Retry on the bank tile when charge_failed)
 *
 * Props `backlogInfo`, `confirmingBacklog`, `onConfirmBacklog`, `onRollback`,
 * `preflightData`, and `preflightLoading` are accepted-and-ignored for
 * backwards-compatibility with existing call sites (MyBanksPage, SettingsPage,
 * BankFeedCenterPage, ConnectionsSettingsTab). They can be deleted from those
 * pages in a follow-up sweep.
 */
export function MonoSyncProgress({
  open,
  syncStatus,
  syncMessage,
  onDismiss,
  onNavigateToBankFeed,
  backlogInfo,
  confirmingBacklog,
  onConfirmBacklog,
  onRollback,
  preflightData,
  preflightLoading,
}) {
  // These props are accepted for backwards-compatibility with existing
  // callers but no longer read - the backlog_jobs orchestrator owns fee
  // charging + retry + preflight logic. Reference them here so linters and
  // type-checkers don't flag them as unused.
  void backlogInfo;
  void confirmingBacklog;
  void onConfirmBacklog;
  void onRollback;
  void preflightData;
  void preflightLoading;

  if (!open) return null;

  const isSyncing = syncStatus === 'syncing';
  const isCompleted = syncStatus === 'completed' || syncStatus === 'no_backlog';
  const isFailed = syncStatus === 'failed';
  const isSyncingInBackground = syncStatus === 'syncing_in_background';
  const isTransactionsUnavailable = syncStatus === 'transactions_unavailable';

  // If sync_status is `backlog_required` we no longer render fee UI - the
  // orchestrator has taken ownership and will surface progress in the global
  // banner. Treat it as `syncing`.
  const showAsSyncing = isSyncing || syncStatus === 'backlog_required';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onDismiss(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="mono-sync-progress-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {showAsSyncing && 'Connecting Your Bank'}
            {isCompleted && 'Bank Connected Successfully'}
            {isSyncingInBackground && 'Bank Connected. Still Syncing'}
            {isTransactionsUnavailable && 'Bank Connected. Upload Required'}
            {isFailed && 'Connection Issue'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {showAsSyncing && (
            <div
              className="flex flex-col items-center gap-4 text-center"
              data-testid="mono-sync-syncing"
            >
              <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {syncMessage || 'Fetching your transaction history…'}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  You can safely close this dialog. A progress banner at the
                  top of every page will keep you posted while the sync runs in
                  the background: the app stays fast, and you can keep
                  working.
                </p>
              </div>
              <div className="flex gap-1.5 mt-2">
                <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: '200ms' }} />
                <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" style={{ animationDelay: '400ms' }} />
              </div>
            </div>
          )}

          {isCompleted && (
            <div
              className="flex flex-col items-center gap-4 text-center"
              data-testid="mono-sync-completed"
            >
              <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {syncMessage || 'Your bank details have been fetched!'}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Check the Bank Transactions Center for transaction classification
                  status.
                </p>
              </div>
            </div>
          )}

          {isTransactionsUnavailable && (
            <div
              className="flex flex-col items-center gap-4 text-center"
              data-testid="mono-sync-transactions-unavailable"
            >
              <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-sm text-blue-700 dark:text-blue-400">
                  Bank linked: transaction history not available
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {syncMessage ||
                    'Your bank confirms it is connected but could not retrieve transaction history for this account. This usually happens on certain corporate, overdraft, or prepaid accounts.'}
                </p>
                <p className="text-xs text-muted-foreground mt-2 font-medium">
                  Please upload your bank statement to populate transactions,
                  or contact support.
                </p>
              </div>
            </div>
          )}

          {isSyncingInBackground && (
            <div
              className="flex flex-col items-center gap-4 text-center"
              data-testid="mono-sync-background"
            >
              <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
              </div>
              <div>
                <p className="font-medium text-sm text-amber-700 dark:text-amber-400">
                  Your bank is connected: backfill is still in progress
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {syncMessage ||
                    'We are still backfilling your transactions. Close this dialog and check the Bank Transactions Center in a few minutes: transactions will appear automatically.'}
                </p>
                <p className="text-xs text-muted-foreground mt-2 font-medium">
                  Please do not disconnect the bank.
                </p>
              </div>
            </div>
          )}

          {isFailed && (
            <div
              className="flex flex-col items-center gap-4 text-center"
              data-testid="mono-sync-failed"
            >
              <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                <Ban className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-sm text-red-700 dark:text-red-400">
                  {syncMessage || 'Failed to fetch bank data'}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Please disconnect the bank and try reconnecting.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {showAsSyncing && (
            <Button
              variant="outline"
              onClick={onDismiss}
              className="w-full"
              data-testid="mono-sync-close-during-sync-btn"
            >
              Close: keep syncing in background
            </Button>
          )}

          {(isCompleted || isFailed || isSyncingInBackground || isTransactionsUnavailable) && (
            <Button
              onClick={() => {
                if (
                  (isCompleted ||
                    isSyncingInBackground ||
                    isTransactionsUnavailable) &&
                  onNavigateToBankFeed
                ) {
                  onDismiss();
                  onNavigateToBankFeed();
                } else {
                  onDismiss();
                }
              }}
              className="w-full"
              data-testid="mono-sync-close-btn"
            >
              {isCompleted ? (
                <>
                  Go to Bank Transactions Center
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : isSyncingInBackground ? (
                <>
                  Got it: check Bank Transactions Center later
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : isTransactionsUnavailable ? (
                <>
                  Upload my bank statement instead
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                'Close'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
